import { useEffect, useMemo, useRef, useState } from 'react'
import { artifactTypes, chapters } from '../data/expedition-v5.js'
import { essaysV6 as expeditionEssays, stationScenes as majorStations, editorialLines, environmentPhases } from '../data/expedition-v6.js'
import { useExpedition } from '../store/useExpedition.js'
import { FieldIndex } from './FieldIndex.jsx'
import { OpeningSequence } from './OpeningSequence.jsx'

const majorIndexes = majorStations.map(station => expeditionEssays.find(essay => essay.id === station.essayId)?.index).filter(Number.isFinite)
const lineOrder = ['craft','laws','people','portfolio','place','culture']

const withPosition = index => {
  const essay = expeditionEssays[index]
  const state = useExpedition.getState()
  const projection = state.routeProjections[essay.theme] || state.ropeProjection
  const peers = expeditionEssays.filter(item=>item.theme===essay.theme)
  const rank = peers.findIndex(item=>item.id===essay.id)
  const t = .04 + (rank/Math.max(1,peers.length-1))*.9
  const position = projection.length ? projection[Math.round(t*(projection.length-1))] : [0,2,-15]
  return {...essay,position}
}

export function Interface({ reducedMotion }) {
  const entered = useExpedition(state => state.entered), enter = useExpedition(state => state.enter)
  const exit = useExpedition(state => state.exit)
  const activeEssay = useExpedition(state => state.activeEssay), selectEssay = useExpedition(state => state.selectEssay)
  const activeTheme = useExpedition(state => state.activeTheme), selectTheme = useExpedition(state => state.selectTheme)
  const freeExplore = useExpedition(state => state.freeExplore), toggleFreeExplore = useExpedition(state => state.toggleFreeExplore)
  const indexOpen = useExpedition(state=>state.indexOpen), toggleIndex=useExpedition(state=>state.toggleIndex), closeIndex=useExpedition(state=>state.closeIndex)
  const photoMode=useExpedition(state=>state.photoMode), togglePhotoMode=useExpedition(state=>state.togglePhotoMode)
  const requestReturnToRope=useExpedition(state=>state.requestReturnToRope)
  const fieldMode=useExpedition(state=>state.fieldMode), setFieldMode=useExpedition(state=>state.setFieldMode)
  const readEssayIds=useExpedition(state=>state.readEssayIds)
  const routeProjections=useExpedition(state=>state.routeProjections)
  const setCameraDirective=useExpedition(state=>state.setCameraDirective), setConditions=useExpedition(state=>state.setConditions)
  const reset = useExpedition(state => state.reset)
  const [playing,setPlaying] = useState(false), [lensesOpen,setLensesOpen] = useState(false)
  const [routeStep,setRouteStep] = useState(0), [routesClimbed,setRoutesClimbed] = useState(0)
  const timer = useRef(null)
  const index = activeEssay?.index ?? 0
  const currentRoute = expeditionEssays.filter(essay=>essay.theme===(activeTheme||activeEssay?.theme))
  const routeRank = activeEssay ? Math.max(0,currentRoute.findIndex(essay=>essay.id===activeEssay.id)) : 0
  const chapter = chapters.find(item => index>=item.start&&index<=item.end) || chapters[0]
  const station = activeEssay ? majorStations.find(item => item.essayId===activeEssay.id) : null
  const routeProgress = currentRoute.length>1 ? routeRank/(currentRoute.length-1) : 0
  const altitude = Math.round((5430+routeProgress*(14259-5430))/10)*10

  useEffect(()=>{
    const id=new URLSearchParams(location.search).get('essay')
    if(!id) return
    const essay=expeditionEssays.find(item=>item.id===id)
    if(!essay||!routeProjections[essay.theme]?.length) return
    useExpedition.setState({entered:true,introPhase:'complete',freeExplore:false,activeTheme:essay.theme})
    selectEssay(withPosition(essay.index))
  },[routeProjections,selectEssay])

  useEffect(() => {
    if(!playing) return clearTimeout(timer.current)
    const theme=activeTheme
    const essaysOnRoute=expeditionEssays.filter(essay=>essay.theme===theme)
    const essay=essaysOnRoute[routeStep]
    if(!theme||!essay){setPlaying(false);return}
    const focused=withPosition(essay.index)
    const phase=environmentPhases.find(item=>item.essayRange&&essay.index>=item.essayRange[0]&&essay.index<=item.essayRange[1])
    if(phase) setConditions(phase.weather,phase.order/Math.max(1,environmentPhases.length-1))
    selectEssay(focused)
    setCameraDirective({
      id:`route-${theme}-${routeStep}-${Date.now()}`,
      position:[focused.position[0]+7,focused.position[1]+5,focused.position[2]+10],
      target:focused.position,
      fov:34,
      speed:.8,
    })
    timer.current=setTimeout(()=>{
      if(routeStep<essaysOnRoute.length-1){
        setRouteStep(routeStep+1)
        return
      }
      if(routesClimbed>=lineOrder.length-1){
        setPlaying(false)
        setCameraDirective(null)
        return
      }
      const nextTheme=lineOrder[(lineOrder.indexOf(theme)+1)%lineOrder.length]
      setRoutesClimbed(routesClimbed+1)
      setRouteStep(0)
      selectTheme(nextTheme)
    },reducedMotion?2200:7000)
    return()=>clearTimeout(timer.current)
  },[activeTheme,playing,reducedMotion,routeStep,routesClimbed,selectEssay,selectTheme,setCameraDirective,setConditions])
  useEffect(()=>{
    if(playing) return
    const phase=environmentPhases.find(item=>item.essayRange&&index>=item.essayRange[0]&&index<=item.essayRange[1])||environmentPhases[0]
    setConditions(phase.weather,phase.order/Math.max(1,environmentPhases.length-1))
  },[index,playing,setConditions])

  const focusEssay = essay => {
    setPlaying(false)
    clearTimeout(timer.current)
    closeIndex()
    const focused=withPosition(essay.index)
    selectTheme(focused.theme)
    selectEssay(focused)
    const peers=expeditionEssays.filter(item=>item.theme===focused.theme)
    setRouteStep(peers.findIndex(item=>item.id===focused.id))
    setCameraDirective({
      id:`essay-${focused.id}-${Date.now()}`,
      position:[focused.position[0]+6.8,focused.position[1]+4.6,focused.position[2]+10.5],
      target:focused.position,
      fov:38,
      speed:1.4,
    })
  }
  const seek = next => focusEssay(expeditionEssays[Math.max(0,Math.min(expeditionEssays.length-1,next))])
  const seekRoute = next => {
    const route=expeditionEssays.filter(essay=>essay.theme===(activeEssay?.theme||activeTheme))
    const essay=route[Math.max(0,Math.min(route.length-1,next))]
    if(essay) focusEssay(essay)
  }
  const startGuided = () => {
    if(!playing){
      if(!activeTheme){
        setLensesOpen(true)
        return
      }
      setCameraDirective(null)
      const essaysOnRoute=expeditionEssays.filter(essay=>essay.theme===activeTheme)
      const startAt=activeEssay?.theme===activeTheme ? Math.max(0,essaysOnRoute.findIndex(essay=>essay.id===activeEssay.id)) : 0
      setRouteStep(startAt)
      setRoutesClimbed(0)
      setPlaying(true)
      return
    }
    clearTimeout(timer.current)
    setPlaying(false)
    setCameraDirective(null)
  }
  const chooseTheme = id => {
    clearTimeout(timer.current)
    setPlaying(false)
    setCameraDirective(null)
    setRouteStep(0)
    setRoutesClimbed(0)
    selectTheme(id)
    setLensesOpen(false)
  }
  const handleExploreToggle = () => { if(freeExplore) setLensesOpen(true); else toggleFreeExplore() }
  const shareView = async()=>{ const essay=useExpedition.getState().activeEssay; const url=new URL(location.href); if(essay) url.searchParams.set('essay',essay.id); else url.searchParams.delete('essay'); await navigator.clipboard?.writeText(url.toString()) }

  return <div className={`interface v6 ${freeExplore?'is-exploring':''} ${photoMode?'photo-mode':''}`}>
    <header className="mast-v5"><button className="wordmark-v5" onClick={exit}>BOULDER <span>VENTURES</span></button><div className="expedition-meta">PERSPECTIVES · SIX LINES · 2020—2026</div></header>

    {!entered && <section className="entry-v5 entry-live">
      <div className="entry-copy"><p className="eyebrow">Boulder Ventures · Perspectives · 2020—2026</p><h1>Read the<br/><em>terrain.</em></h1><p>Fifty-five field notes leave Pearl Street and climb six lines through Boulder’s home terrain.</p><button onClick={enter}>Begin at Pearl Street <span>↗</span></button></div>
    </section>}

    {entered && <>
      <OpeningSequence reducedMotion={reducedMotion}/>
      <aside className="chapter-v5"><span>CHAPTER {String(chapter.order+1).padStart(2,'0')} / 09</span><strong>{chapter.label}</strong><p>{chapter.thesis}</p></aside>

      <button className={`compass-control ${lensesOpen?'open':''}`} onClick={()=>setLensesOpen(value=>!value)} aria-label="Open thematic compass"><img src="./brand/compass-blue.png" alt="" /><span>Read another layer</span></button>
      {lensesOpen && <nav className="lens-menu"><p>Choose a line</p>{lineOrder.map((id,index)=>{const item=editorialLines[id],count=expeditionEssays.filter(essay=>essay.theme===id).length;return <button key={id} className={activeTheme===id?'active':''} onClick={()=>chooseTheme(id)} style={{'--lens':item.color}}><i></i><span>{String(index+1).padStart(2,'0')} · {item.name}</span><small>{count} essays</small></button>})}</nav>}

      {activeEssay && <article className={`field-note-v5 ${station?'major':''}`}>
        <button className="close-note" onClick={()=>selectEssay(null)}>×</button><p className="note-meta">{activeEssay.date} · {artifactTypes[activeEssay.theme].singular} · {altitude.toLocaleString()} FT</p><h2>{activeEssay.title}</h2><blockquote>{station?.headline || activeEssay.takeaway}</blockquote>{station && <p className="editorial">{station.detail}</p>}<div className="artifact-name" style={{color:editorialLines[activeEssay.theme].color}}>{editorialLines[activeEssay.theme].interaction.verb}</div><div className="note-actions"><button onClick={()=>seekRoute(routeRank-1)} disabled={routeRank===0}>←</button><a href={activeEssay.url} target="_blank" rel="noreferrer">Read the essay ↗</a><button onClick={()=>seekRoute(routeRank+1)} disabled={routeRank===currentRoute.length-1}>→</button></div>
      </article>}

      <section className="journey-v5"><button onClick={startGuided}>{playing?'Ⅱ PAUSE':activeTheme?'▶ CLIMB THIS ROUTE':'CHOOSE A ROUTE'}</button><input type="range" min="0" max={Math.max(0,currentRoute.length-1)} value={Math.min(routeRank,Math.max(0,currentRoute.length-1))} disabled={!currentRoute.length} onChange={event=>seekRoute(+event.target.value)} /><div><span>PEARL STREET</span><b>{activeTheme?`${editorialLines[activeTheme].name.toUpperCase()} · ${routeRank+1} / ${currentRoute.length}`:'6 ROUTES'}</b><span>HIGH POINT</span></div></section>
      {playing&&<div className="documentary-caption"><span>{String(routeStep+1).padStart(2,'0')} / {currentRoute.length}</span><strong>{editorialLines[activeTheme]?.name} · climbing one line from base to high point</strong><button onClick={()=>setRouteStep(Math.min(currentRoute.length-1,routeStep+1))}>Next ↗</button></div>}
      <div className="field-altitude"><span>FIELD ELEVATION</span><strong>{altitude.toLocaleString()}′</strong><small>{readEssayIds.length} / 55 VISITED</small></div>
      <div className="utility-v5"><button onClick={reset}>Reset</button><button className={freeExplore?'active':''} onClick={handleExploreToggle}>{freeExplore?'Find a route':'Free roam'}</button><button onClick={()=>setFieldMode(fieldMode==='fly'?'hike':'fly')}>{fieldMode==='fly'?'Hike':'Fly'}</button><button onClick={toggleIndex}>Field index</button><button onClick={shareView}>Share</button><button onClick={togglePhotoMode}>Photo</button>{freeExplore&&activeTheme&&<button onClick={requestReturnToRope}>Find the rope</button>}<span>{freeExplore?(fieldMode==='hike'?'GROUND MODE · WASD: HIKE · DRAG: LOOK':'FLIGHT MODE · WASD/QE · DRAG: ORBIT'):'DRAG: SPIN · SCROLL: ZOOM'}</span></div>
      {indexOpen&&<FieldIndex onChoose={seek}/>} 
      {photoMode&&<button className="photo-exit" onClick={togglePhotoMode}>Exit photograph mode</button>}
    </>}
  </div>
}
